import React, { useState, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Switch } from '@/components/ui/switch';
import { CheckCircle2 } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { format } from 'date-fns';
import { useBankAccounts } from '@/hooks/use-bank-accounts';
import { useVatRate, useActiveWithholdingRates } from '@/hooks/finance/useTaxSettings';

interface AdvancePaymentConfirmationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  procurement: any;
  onConfirm: () => void;
  paymentType: 'advance' | 'remaining';
}

type VatMode = 'none' | 'add' | 'deduct';

export const AdvancePaymentConfirmationDialog: React.FC<AdvancePaymentConfirmationDialogProps> = ({
  open,
  onOpenChange,
  procurement,
  onConfirm,
  paymentType
}) => {
  const [actualPaymentDate, setActualPaymentDate] = useState(new Date().toISOString().split('T')[0]);
  const [confirmationNotes, setConfirmationNotes] = useState('');
  const [paidFromAccountId, setPaidFromAccountId] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Tax handling state (only for remaining payment since that creates the expense)
  const [vatMode, setVatMode] = useState<VatMode>('none');
  const [withholdingApplicable, setWithholdingApplicable] = useState(false);
  const [selectedWithholdingRateId, setSelectedWithholdingRateId] = useState<string>('');
  
  const { toast } = useToast();
  const { user } = useAuth();
  const { data: bankAccounts, isLoading: bankAccountsLoading } = useBankAccounts(true);
  const { data: defaultVatRate } = useVatRate();
  const { data: withholdingRates } = useActiveWithholdingRates();

  const isAdvancePayment = paymentType === 'advance';
  const amount = isAdvancePayment 
    ? procurement.finance?.advance_payment_amount 
    : procurement.finance?.remaining_payment_amount;

  // For expense creation on remaining payment, use remaining_payment_amount (not full approved amount)
  const approvedAmount = procurement.finance?.remaining_payment_amount || 0;

  // Tax calculations (only used for remaining payment expense creation)
  const paymentTotals = useMemo(() => {
    const vatRate = defaultVatRate ?? 15;
    const selectedWht = withholdingRates?.find(r => r.id === selectedWithholdingRateId);
    const withholdingRate = withholdingApplicable && selectedWht ? selectedWht.rate : 0;
    
    let baseAmount: number;
    let vatAmount: number;
    let subtotal: number;
    
    switch (vatMode) {
      case 'add':
        baseAmount = approvedAmount;
        vatAmount = baseAmount * (vatRate / 100);
        subtotal = baseAmount + vatAmount;
        break;
      case 'deduct':
        subtotal = approvedAmount;
        baseAmount = subtotal / (1 + vatRate / 100);
        vatAmount = subtotal - baseAmount;
        break;
      case 'none':
      default:
        baseAmount = approvedAmount;
        vatAmount = 0;
        subtotal = approvedAmount;
        break;
    }
    
    const withholdingAmount = baseAmount * (withholdingRate / 100);
    const netPayment = subtotal - withholdingAmount;
    
    return {
      baseAmount: Math.round(baseAmount * 100) / 100,
      vatRate: vatMode === 'none' ? 0 : vatRate,
      vatAmount: Math.round(vatAmount * 100) / 100,
      subtotal: Math.round(subtotal * 100) / 100,
      withholdingRate,
      withholdingAmount: Math.round(withholdingAmount * 100) / 100,
      netPayment: Math.round(netPayment * 100) / 100,
    };
  }, [approvedAmount, vatMode, defaultVatRate, withholdingApplicable, selectedWithholdingRateId, withholdingRates]);

  const handleConfirmPayment = async () => {
    if (!user?.id) return;

    setIsSubmitting(true);
    try {
      const updateData: any = {};
      
      if (isAdvancePayment) {
        updateData.advance_payment_confirmed = true;
        updateData.advance_payment_confirmed_by = user.id;
        updateData.advance_payment_confirmed_at = new Date().toISOString();
        updateData.advance_payment_date = actualPaymentDate;
        updateData.advance_paid_from_account_id = paidFromAccountId || null;
        if (confirmationNotes) updateData.advance_payment_notes = confirmationNotes;
      } else {
        updateData.remaining_payment_confirmed = true;
        updateData.remaining_payment_confirmed_by = user.id;
        updateData.remaining_payment_confirmed_at = new Date().toISOString();
        updateData.remaining_payment_date = actualPaymentDate;
        updateData.remaining_paid_from_account_id = paidFromAccountId || null;
        if (confirmationNotes) updateData.remaining_payment_notes = confirmationNotes;
      }

      const { error } = await supabase
        .from('procurement_finance')
        .update(updateData)
        .eq('procurement_id', procurement.id);

      if (error) throw error;

      // Check if we need to update procurement status and create expense
      const result = await checkAndUpdateProcurementStatus(procurement.id, isAdvancePayment, actualPaymentDate);

      toast({
        title: "Payment Confirmed",
        description: `The ${isAdvancePayment ? 'advance' : 'remaining'} payment has been marked as confirmed.${result.statusUpdated ? ' Procurement completed.' : ''}${result.expenseCreated ? ' Expense record created.' : ''}`,
      });

      onConfirm();
      onOpenChange(false);
      
      // Reset form
      setActualPaymentDate(new Date().toISOString().split('T')[0]);
      setConfirmationNotes('');
      setPaidFromAccountId('');
      setVatMode('none');
      setWithholdingApplicable(false);
      setSelectedWithholdingRateId('');
    } catch (error) {
      console.error('Error confirming payment:', error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to confirm payment.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const checkAndUpdateProcurementStatus = async (
    procurementId: string, 
    isAdvancePayment: boolean,
    paymentDate: string
  ): Promise<{ statusUpdated: boolean; expenseCreated: boolean }> => {
    try {
      // Get current finance data
      const { data: financeData, error: financeError } = await supabase
        .from('procurement_finance')
        .select('*')
        .eq('procurement_id', procurementId)
        .single();

      if (financeError) throw financeError;

      // Check if both advance and remaining payments are confirmed (for advance payment scenarios)
      if (financeData.has_advance_payment) {
        // Re-check with updated state
        const { data: updatedFinance } = await supabase
          .from('procurement_finance')
          .select('advance_payment_confirmed, remaining_payment_confirmed')
          .eq('procurement_id', procurementId)
          .single();
        
        const allPaymentsComplete = updatedFinance?.advance_payment_confirmed && updatedFinance?.remaining_payment_confirmed;
        
        if (allPaymentsComplete) {
          // Create expense record for the full approved amount with tax breakdown
          const expenseCreated = await createExpenseForProcurement(
            procurementId,
            financeData,
            paymentDate,
            user!.id,
            paidFromAccountId || undefined
          );
          
          // Update procurement status to completed
          const { error: statusError } = await supabase
            .from('procurements')
            .update({ status: 'completed' })
            .eq('id', procurementId);

          if (statusError) throw statusError;
          return { statusUpdated: true, expenseCreated };
        }
      }

      return { statusUpdated: false, expenseCreated: false };
    } catch (error) {
      console.error('Error updating procurement status:', error);
      return { statusUpdated: false, expenseCreated: false };
    }
  };

  const createExpenseForProcurement = async (
    procurementId: string,
    financeData: any,
    paymentDate: string,
    userId: string,
    bankAccountId?: string
  ): Promise<boolean> => {
    try {
      // Generate expense number
      const { data: expenseNumber, error: numberError } = await supabase
        .rpc('generate_expense_number');
      
      if (numberError || !expenseNumber) {
        console.error('Error generating expense number:', numberError);
        return false;
      }

      // Create expense record with full tax breakdown
      const { data: expense, error: expenseError } = await supabase
        .from('expenses')
        .insert({
          expense_number: expenseNumber,
          expense_date: paymentDate,
          description: `Remaining payment - Procurement: ${procurement.product_name || 'Product'} from ${procurement.source_name || 'Supplier'}`,
          vendor_name: procurement.source_name || 'Unknown Supplier',
          reference_number: `PROC-${procurementId.slice(0, 8)}`,
          amount: paymentTotals.baseAmount,
          currency: procurement.currency || 'ETB',
          vat_rate: paymentTotals.vatRate,
          vat_amount: paymentTotals.vatAmount,
          total_amount: paymentTotals.subtotal,
          withholding_rate: paymentTotals.withholdingRate,
          withholding_amount: paymentTotals.withholdingAmount,
          net_payment: paymentTotals.netPayment,
          payment_status: 'paid',
          payment_date: paymentDate,
          created_by: userId,
          approved_by: userId,
          approved_at: new Date().toISOString(),
          bank_account_id: bankAccountId || null
        })
        .select('id')
        .single();

      if (expenseError) {
        console.error('Error creating expense:', expenseError);
        return false;
      }

      // Link expense to procurement finance
      await supabase
        .from('procurement_finance')
        .update({ expense_id: expense.id })
        .eq('procurement_id', procurementId);

      console.log('Created expense for procurement:', expense.id);
      return true;
    } catch (error) {
      console.error('Error creating expense for procurement:', error);
      return false;
    }
  };

  // Show tax options only for remaining payment (when expense will be created)
  const showTaxOptions = !isAdvancePayment;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CheckCircle2 className="h-5 w-5 text-green-600" />
            Confirm {isAdvancePayment ? 'Advance' : 'Remaining'} Payment
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          <div className="bg-muted p-4 rounded-md">
            <h4 className="font-medium mb-2">Payment Details</h4>
            <div className="space-y-1 text-sm">
              <div><span className="font-medium">Product:</span> {procurement.product_name || 'N/A'}</div>
              <div><span className="font-medium">Payment Type:</span> {isAdvancePayment ? 'Advance Payment' : 'Remaining Payment'}</div>
              <div><span className="font-medium">Amount:</span> Br{amount?.toFixed(2) || '0.00'}</div>
              {procurement.finance?.payment_due_date && (
                <div><span className="font-medium">Due Date:</span> {format(new Date(procurement.finance.payment_due_date), 'PP')}</div>
              )}
              {!isAdvancePayment && (
                <div className="pt-2 border-t mt-2">
                  <span className="font-medium">Total Approved Amount:</span> Br{approvedAmount.toFixed(2)}
                </div>
              )}
            </div>
          </div>

          {/* Tax options only for remaining payment */}
          {showTaxOptions && (
            <>
              {/* VAT Mode Selection */}
              <div className="space-y-3 border rounded-md p-4">
                <Label className="text-base font-medium">VAT Handling (for expense record)</Label>
                <RadioGroup value={vatMode} onValueChange={(v) => setVatMode(v as VatMode)} className="space-y-2">
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="none" id="vat-none" />
                    <Label htmlFor="vat-none" className="font-normal cursor-pointer">No VAT</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="add" id="vat-add" />
                    <Label htmlFor="vat-add" className="font-normal cursor-pointer">Add VAT ({defaultVatRate ?? 15}%)</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="deduct" id="vat-deduct" />
                    <Label htmlFor="vat-deduct" className="font-normal cursor-pointer">Deduct VAT (amount is VAT-inclusive)</Label>
                  </div>
                </RadioGroup>
              </div>

              {/* Withholding Tax */}
              <div className="space-y-3 border rounded-md p-4">
                <div className="flex items-center justify-between">
                  <Label className="text-base font-medium">Withholding Tax</Label>
                  <Switch checked={withholdingApplicable} onCheckedChange={setWithholdingApplicable} />
                </div>
                {withholdingApplicable && (
                  <Select value={selectedWithholdingRateId} onValueChange={setSelectedWithholdingRateId}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select withholding rate" />
                    </SelectTrigger>
                    <SelectContent>
                      {withholdingRates?.map((rate) => (
                        <SelectItem key={rate.id} value={rate.id}>
                          {rate.name} ({rate.rate}%)
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>

              {/* Tax Summary */}
              <div className="bg-muted/50 border rounded-md p-4 space-y-2 text-sm">
                <h4 className="font-medium">Expense Summary</h4>
                <div className="space-y-1">
                  <div className="flex justify-between">
                    <span>Base Amount:</span>
                    <span>Br{paymentTotals.baseAmount.toFixed(2)}</span>
                  </div>
                  {paymentTotals.vatRate > 0 && (
                    <div className="flex justify-between text-muted-foreground">
                      <span>VAT ({paymentTotals.vatRate}%):</span>
                      <span>Br{paymentTotals.vatAmount.toFixed(2)}</span>
                    </div>
                  )}
                  <div className="flex justify-between border-t pt-1">
                    <span>Subtotal:</span>
                    <span>Br{paymentTotals.subtotal.toFixed(2)}</span>
                  </div>
                  {paymentTotals.withholdingRate > 0 && (
                    <div className="flex justify-between text-muted-foreground">
                      <span>Withholding ({paymentTotals.withholdingRate}%):</span>
                      <span>-Br{paymentTotals.withholdingAmount.toFixed(2)}</span>
                    </div>
                  )}
                  <div className="flex justify-between font-medium border-t pt-1">
                    <span>Net Payment:</span>
                    <span className="text-green-600">Br{paymentTotals.netPayment.toFixed(2)}</span>
                  </div>
                </div>
              </div>
            </>
          )}

          <div className="space-y-2">
            <Label htmlFor="paidFromAccount">Paid From Account *</Label>
            <Select value={paidFromAccountId} onValueChange={setPaidFromAccountId}>
              <SelectTrigger id="paidFromAccount">
                <SelectValue placeholder={bankAccountsLoading ? "Loading accounts..." : "Select bank account"} />
              </SelectTrigger>
              <SelectContent>
                {bankAccounts?.map((account) => (
                  <SelectItem key={account.id} value={account.id}>
                    {account.name} {account.bank_name ? `(${account.bank_name})` : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="actualPaymentDate">Actual Payment Date</Label>
            <Input
              id="actualPaymentDate"
              type="date"
              value={actualPaymentDate}
              onChange={(e) => setActualPaymentDate(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="confirmationNotes">Confirmation Notes (Optional)</Label>
            <Textarea
              id="confirmationNotes"
              placeholder="Add any notes about the payment confirmation..."
              value={confirmationNotes}
              onChange={(e) => setConfirmationNotes(e.target.value)}
              rows={3}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleConfirmPayment} disabled={isSubmitting || !paidFromAccountId}>
            {isSubmitting ? 'Confirming...' : 'Confirm Payment'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
